import React, { useCallback, useEffect, useMemo, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  ClipboardCheck,
  Loader2,
  RefreshCcw,
  AlertTriangle,
  CheckCircle2,
  Search,
} from "lucide-react";
import { toast } from "sonner";

const STYLE_BADGE = {
  MODERN: "bg-sky-500/15 text-sky-300 border-sky-500/30",
  CLASSIC: "bg-amber-500/15 text-amber-300 border-amber-500/30",
  NEOCLASSIC: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  ART_DECO: "bg-rose-500/15 text-rose-300 border-rose-500/30",
};

/**
 * Admin tab — compares the uploaded DB photos against the official
 * BD_2D_new.xlsx product manifest. Surfaces missing codes per
 * (sim_type, style, category) so FDP staff know exactly what to upload next.
 */
export default function CatalogAuditTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("missing"); // missing | all | complete
  const [search, setSearch] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: resp } = await api.get("/admin/catalog/audit");
      setData(resp);
    } catch (e) {
      toast.error(
        formatApiErrorDetail(e.response?.data?.detail) ||
          "Eroare la încărcare audit",
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const rows = useMemo(() => {
    if (!data?.rows) return [];
    const q = search.trim().toLowerCase();
    return data.rows.filter((r) => {
      if (filter === "missing" && r.missing.length === 0) return false;
      if (filter === "complete" && r.missing.length > 0) return false;
      if (q) {
        const hay =
          `${r.category_name} ${r.category_slug} ${r.style} ${r.sim_type} ${r.missing.join(" ")} ${r.extra.join(" ")}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [data, filter, search]);

  if (loading && !data) {
    return (
      <div className="py-20 flex items-center justify-center text-zinc-500">
        <Loader2 className="animate-spin mr-2" size={18} />
        Se încarcă audit-ul...
      </div>
    );
  }

  const sum = data?.summary || {};
  const totalMissing = (sum.INTERIOR?.missing || 0) + (sum.EXTERIOR?.missing || 0);

  return (
    <div className="space-y-6" data-testid="catalog-audit-tab">
      {/* Header + summary */}
      <div className="rounded-xl border border-white/10 bg-zinc-900/30 p-5">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h2 className="text-xl font-semibold text-white mb-1 flex items-center gap-2">
              <ClipboardCheck className="text-orange-400" size={20} />
              Audit catalog — DB vs Excel BD_2D_new
            </h2>
            <p className="text-sm text-zinc-400 leading-relaxed max-w-2xl">
              Compară pozele uploadate în baza de date cu codurile așteptate din spreadsheet-ul oficial FDP. „Missing" = poze pe care încă trebuie să le încarci. „Extra" = poze încărcate dar cu nume diferit față de Excel (deobicei typo în filename) sau profile noi.
            </p>
          </div>
          <button
            onClick={load}
            className="p-2 rounded-md border border-white/10 hover:bg-white/5 text-zinc-300 shrink-0"
            data-testid="audit-refresh"
            title="Reîncarcă"
          >
            <RefreshCcw size={14} />
          </button>
        </div>

        {/* Summary tiles */}
        <div className="grid md:grid-cols-2 gap-3 mt-4">
          {["INTERIOR", "EXTERIOR"].map((t) => {
            const s = sum[t] || {};
            const pct = s.completion ?? 100;
            return (
              <div
                key={t}
                className="rounded-lg border border-white/10 bg-zinc-950/40 p-4"
                data-testid={`audit-summary-${t.toLowerCase()}`}
              >
                <div className="flex items-center justify-between mb-3">
                  <span className="font-outfit font-bold text-white text-lg">
                    {t === "INTERIOR" ? "Interior" : "Exterior"}
                  </span>
                  <span
                    className={`text-2xl font-black font-outfit ${
                      pct === 100
                        ? "text-emerald-400"
                        : pct >= 90
                        ? "text-orange-400"
                        : "text-rose-400"
                    }`}
                  >
                    {pct}%
                  </span>
                </div>
                <div className="h-2 bg-zinc-900 rounded-full overflow-hidden mb-3">
                  <div
                    className={`h-full transition-all ${
                      pct === 100
                        ? "bg-emerald-500"
                        : pct >= 90
                        ? "bg-orange-500"
                        : "bg-rose-500"
                    }`}
                    style={{ width: `${pct}%` }}
                  />
                </div>
                <div className="grid grid-cols-4 gap-2 text-center">
                  <div>
                    <div className="text-white font-bold text-base">{s.expected || 0}</div>
                    <div className="text-[9px] uppercase tracking-wider text-zinc-500">Așteptate</div>
                  </div>
                  <div>
                    <div className="text-emerald-400 font-bold text-base">{s.present || 0}</div>
                    <div className="text-[9px] uppercase tracking-wider text-zinc-500">Prezente</div>
                  </div>
                  <div>
                    <div className="text-rose-400 font-bold text-base">{s.missing || 0}</div>
                    <div className="text-[9px] uppercase tracking-wider text-zinc-500">Lipsă</div>
                  </div>
                  <div>
                    <div className="text-amber-400 font-bold text-base">{s.extra || 0}</div>
                    <div className="text-[9px] uppercase tracking-wider text-zinc-500">Extra</div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {totalMissing === 0 && (
          <div className="mt-3 flex items-center gap-2 text-emerald-400 text-sm">
            <CheckCircle2 size={16} />
            <span>Catalog complet! Toate codurile din Excel sunt prezente.</span>
          </div>
        )}
      </div>

      {/* Filter + search */}
      <div className="rounded-xl border border-white/10 bg-zinc-900/30 p-4">
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-1 text-xs">
            {[
              { id: "missing", label: `Cu lipsă (${data?.rows?.filter((r) => r.missing.length > 0).length || 0})` },
              { id: "complete", label: `Complete (${data?.rows?.filter((r) => r.missing.length === 0).length || 0})` },
              { id: "all", label: `Toate (${data?.rows?.length || 0})` },
            ].map((f) => (
              <button
                key={f.id}
                onClick={() => setFilter(f.id)}
                className={`px-3 py-1.5 rounded-md transition ${
                  filter === f.id
                    ? "bg-orange-500 text-white"
                    : "bg-zinc-900/60 text-zinc-400 hover:text-white"
                }`}
                data-testid={`audit-filter-${f.id}`}
              >
                {f.label}
              </button>
            ))}
          </div>
          <div className="flex-1 flex items-center gap-2 px-3 py-1.5 rounded-md border border-white/10 bg-zinc-950/40 min-w-[200px]">
            <Search size={13} className="text-zinc-500" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Caută slug / cod / stil..."
              className="bg-transparent flex-1 text-sm text-white placeholder:text-zinc-500 outline-none"
              data-testid="audit-search"
            />
          </div>
        </div>
      </div>

      {/* Rows */}
      <div className="rounded-xl border border-white/10 bg-zinc-900/30 p-4">
        {rows.length === 0 ? (
          <p className="text-sm text-zinc-500 italic py-8 text-center">
            Niciun rezultat. Schimbă filtrul sau căutarea.
          </p>
        ) : (
          <div className="space-y-2">
            {rows.map((r) => (
              <div
                key={`${r.sim_type}-${r.style}-${r.category_slug}`}
                className={`rounded-lg border p-3 ${
                  r.missing.length > 0
                    ? "border-rose-500/20 bg-rose-500/5"
                    : "border-white/10 bg-zinc-950/40"
                }`}
                data-testid={`audit-row-${r.sim_type}-${r.style}-${r.category_slug}`}
              >
                <div className="flex items-center gap-3 mb-2 flex-wrap">
                  <span className="font-outfit font-bold text-white text-sm">
                    {r.category_name}
                  </span>
                  <span
                    className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded border ${
                      STYLE_BADGE[r.style] || "bg-zinc-800 text-zinc-400 border-zinc-700"
                    }`}
                  >
                    {r.style}
                  </span>
                  <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-zinc-800/80 text-zinc-400">
                    {r.sim_type}
                  </span>
                  <span className="text-xs text-zinc-500 ml-auto">
                    {r.present_count}/{r.expected_count} ·{" "}
                    <span
                      className={
                        r.completion === 100
                          ? "text-emerald-400"
                          : r.completion >= 90
                          ? "text-orange-400"
                          : "text-rose-400"
                      }
                    >
                      {r.completion}%
                    </span>
                  </span>
                </div>

                {r.missing.length > 0 && (
                  <div className="mb-2">
                    <div className="text-[10px] uppercase tracking-wider text-rose-400 mb-1 flex items-center gap-1">
                      <AlertTriangle size={10} />
                      Lipsesc {r.missing.length}{" "}
                      {r.missing.length === 1 ? "cod" : "coduri"}
                    </div>
                    <div className="flex flex-wrap gap-1">
                      {r.missing.map((c) => (
                        <code
                          key={c}
                          className="text-[11px] font-mono bg-rose-500/15 text-rose-300 px-1.5 py-0.5 rounded border border-rose-500/30"
                        >
                          {c}
                        </code>
                      ))}
                    </div>
                  </div>
                )}

                {r.extra.length > 0 && (
                  <details className="mt-1">
                    <summary className="text-[10px] uppercase tracking-wider text-amber-400 cursor-pointer hover:text-amber-300">
                      {r.extra.length} cod{r.extra.length === 1 ? "" : "uri"} extra (poate fi typo în filename) — vezi
                    </summary>
                    <div className="flex flex-wrap gap-1 mt-1.5">
                      {r.extra.map((c) => (
                        <code
                          key={c}
                          className="text-[11px] font-mono bg-amber-500/10 text-amber-300 px-1.5 py-0.5 rounded border border-amber-500/20"
                        >
                          {c}
                        </code>
                      ))}
                    </div>
                  </details>
                )}

                {r.present.length > 0 && r.missing.length === 0 && r.extra.length === 0 && (
                  <div className="text-[11px] text-emerald-400/80 flex items-center gap-1">
                    <CheckCircle2 size={10} /> Toate codurile prezente
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
